/**
 * CareLogix™ ORACLE — AdminOne Content Generator™ dashboard (AdminOne-only).
 *
 * Internal media repurposing: upload → rights → process → review → approve → export.
 * NO social-publishing controls exist anywhere on this surface (by design).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { getAdminToken } from "@/api/adminOneClient";

const BACKEND = (typeof process !== "undefined" && process.env && process.env.REACT_APP_BACKEND_URL) || "";
const CG = `${BACKEND}/api/v2/adminone/content-generator`;

async function api(path, { method = "GET", body, form } = {}) {
  const headers = {};
  const t = getAdminToken();
  if (t) headers.Authorization = `Bearer ${t}`;
  let payload;
  if (form) payload = form;
  else if (body) { headers["Content-Type"] = "application/json"; payload = JSON.stringify(body); }
  const r = await fetch(`${CG}${path}`, { method, headers, body: payload });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(data?.detail?.status || `HTTP ${r.status}`), { data });
  return data;
}

const RISK_COLOR = { LOW: "#8fdc76", MEDIUM: "#e8a020", HIGH: "#e07a6a", BLOCKED: "#e0483a" };
const S = {
  page: { padding: "24px 28px 90px", background: "linear-gradient(160deg,#050b09 0%,#06231b 60%,#050b09 100%)", color: "#dfe3eb", minHeight: "100vh", fontFamily: "'Figtree','Inter',system-ui,sans-serif" },
  eyebrow: { color: "#c5a059", fontFamily: "'Space Mono',monospace", fontSize: 11, letterSpacing: "0.18em" },
  title: { color: "#fde68a", margin: "2px 0 0", fontSize: 26, fontFamily: "'Cinzel',serif" },
  sectionTitle: { color: "#fde68a", fontFamily: "'Cinzel',serif", fontSize: 18, margin: "26px 0 10px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 12 },
  card: { border: "1px solid rgba(197,160,89,.22)", borderRadius: 14, padding: "14px 16px", background: "rgba(6,26,20,.65)" },
  kpi: { fontSize: 26, fontFamily: "'Space Mono',monospace", color: "#fde68a" },
  kpiLabel: { fontSize: 11, color: "#98a2b6", letterSpacing: ".08em", textTransform: "uppercase" },
  input: { width: "100%", padding: "9px 11px", borderRadius: 9, border: "1px solid rgba(197,160,89,.3)", background: "#04140d", color: "#e8ecf4", fontSize: 14, marginTop: 6, boxSizing: "border-box" },
  btn: { padding: "9px 16px", borderRadius: 9, border: "1px solid rgba(245,200,66,.5)", background: "linear-gradient(180deg,#f5c842,#c99a1f)", color: "#1b1304", fontWeight: 700, cursor: "pointer" },
  btnGhost: { padding: "8px 14px", borderRadius: 9, border: "1px solid rgba(197,160,89,.4)", background: "transparent", color: "#c5a059", cursor: "pointer" },
  pill: (c) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 999, fontSize: 10.5, fontFamily: "'Space Mono',monospace", border: `1px solid ${c}55`, color: c, background: `${c}14` }),
  row: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "12px 14px", border: "1px solid rgba(197,160,89,.18)", borderRadius: 12, marginBottom: 8, background: "rgba(6,26,20,.55)" },
  modalWrap: { position: "fixed", inset: 0, background: "rgba(2,8,6,.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000 },
  modal: { width: "min(560px,92vw)", background: "#06231b", border: "1px solid rgba(197,160,89,.35)", borderRadius: 16, padding: 22 },
  note: { fontSize: 12, color: "#98a2b6", marginTop: 6 },
};

function KPI({ label, value, tid }) {
  return (<div style={S.card} data-testid={tid}><div style={S.kpi}>{value ?? "—"}</div><div style={S.kpiLabel}>{label}</div></div>);
}

export default function AdminOneContentGenerator() {
  const [dash, setDash] = useState(null);
  const [health, setHealth] = useState(null);
  const [projects, setProjects] = useState([]);
  const [queue, setQueue] = useState([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", clips: 3, minS: 15, maxS: 30, caption: "CARELOGIX_GREEN_GOLD" });
  const [file, setFile] = useState(null);
  const [approveClip, setApproveClip] = useState(null);
  const [confirmChk, setConfirmChk] = useState(false);
  const [rationale, setRationale] = useState("");
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [d, h, p, q] = await Promise.all([
        api("/dashboard"), api("/health"), api("/projects"), api("/review-queue"),
      ]);
      setDash(d); setHealth(h); setProjects(p.projects || []); setQueue(q.queue || []);
    } catch (e) { setMsg(`Load error: ${e.message}`); }
  }, []);

  useEffect(() => { refresh(); pollRef.current = setInterval(refresh, 5000); return () => clearInterval(pollRef.current); }, [refresh]);

  const createAndProcess = useCallback(async () => {
    if (!form.name.trim() || !file) { setMsg("Project name and a video file are required."); return; }
    setBusy(true); setMsg("Creating project…");
    try {
      const proj = await api("/projects", { method: "POST", body: {
        name: form.name, requested_clip_count: Number(form.clips),
        minimum_duration_seconds: Number(form.minS), maximum_duration_seconds: Number(form.maxS),
        caption_style: form.caption } });
      setMsg("Uploading video…");
      await api(`/projects/${proj.project_id}/upload-session`, { method: "POST" });
      const fd = new FormData(); fd.append("file", file);
      await api(`/projects/${proj.project_id}/complete-upload`, { method: "POST", form: fd });
      setMsg("Confirming internal rights…");
      await api(`/projects/${proj.project_id}/confirm-rights`, { method: "POST", body: {
        declaration: "CareLogix owns or has permission to use this content.", department: "Internal" } });
      setMsg("Processing started — transcription → analysis → render…");
      await api(`/projects/${proj.project_id}/process`, { method: "POST" });
      setForm({ name: "", clips: 3, minS: 15, maxS: 30, caption: "CARELOGIX_GREEN_GOLD" }); setFile(null);
      refresh();
    } catch (e) { setMsg(`Error: ${e.message}`); }
    finally { setBusy(false); }
  }, [form, file, refresh]);

  const doApprove = useCallback(async () => {
    if (!approveClip) return;
    const needsRationale = ["MEDIUM", "HIGH"].includes(approveClip?.safety?.risk);
    if (!confirmChk) { setMsg("You must check the confirmation box to approve."); return; }
    if (needsRationale && !rationale.trim()) { setMsg("Rationale required for this risk level."); return; }
    try {
      await api(`/clips/${approveClip.clip_id}/approve`, { method: "POST", body: { confirm: true, rationale } });
      await api(`/clips/${approveClip.clip_id}/export`, { method: "POST" });
      setApproveClip(null); setConfirmChk(false); setRationale(""); setMsg("Clip approved and exported."); refresh();
    } catch (e) { setMsg(`Error: ${e.message}`); }
  }, [approveClip, confirmChk, rationale, refresh]);

  const doReject = useCallback(async (clip) => {
    try { await api(`/clips/${clip.clip_id}/reject`, { method: "POST", body: { rationale: "Rejected in review" } }); refresh(); }
    catch (e) { setMsg(`Error: ${e.message}`); }
  }, [refresh]);

  const m = dash?.metrics || {};
  return (
    <div style={S.page} data-testid="adminone-content-generator-page">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={S.eyebrow}>ADMINONE · INTERNAL MEDIA TOOL</div>
          <h1 style={S.title}>Content Generator™</h1>
          <div style={S.note}>Upload → Transcribe → AI Clip → Reframe → Caption → Review → <b>Human Approval</b> → Export. No auto-publishing.</div>
        </div>
        <button type="button" style={S.btnGhost} onClick={refresh} data-testid="cg-refresh">Refresh</button>
      </div>

      {msg && <div style={{ ...S.note, color: "#fde68a", marginTop: 10 }} data-testid="cg-message">{msg}</div>}

      {/* Overview */}
      <div style={S.sectionTitle}>Overview</div>
      <div style={S.grid} data-testid="cg-overview">
        <KPI label="Active Projects" value={m.active_projects} tid="cg-kpi-active" />
        <KPI label="Processing" value={m.videos_processing} tid="cg-kpi-processing" />
        <KPI label="Clips Generated" value={m.clips_generated} tid="cg-kpi-clips" />
        <KPI label="Awaiting Approval" value={m.clips_awaiting_approval} tid="cg-kpi-awaiting" />
        <KPI label="Approved" value={m.approved_clips} tid="cg-kpi-approved" />
        <KPI label="Rejected" value={m.rejected_clips} tid="cg-kpi-rejected" />
        <KPI label="Failed Jobs" value={m.failed_jobs} tid="cg-kpi-failed" />
        <KPI label="Queue Depth" value={m.queue_depth} tid="cg-kpi-queue" />
      </div>

      {/* New Project */}
      <div style={S.sectionTitle}>New Video Project</div>
      <div style={S.card} data-testid="cg-new-project">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 }}>
          <label style={S.kpiLabel}>Project name
            <input style={S.input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cg-input-name" /></label>
          <label style={S.kpiLabel}>Video file (mp4/mov/mkv/webm/m4v)
            <input type="file" accept=".mp4,.mov,.mkv,.webm,.m4v" style={S.input} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cg-input-file" /></label>
          <label style={S.kpiLabel}>Clip count
            <input type="number" min="1" max="20" style={S.input} value={form.clips} onChange={(e) => setForm({ ...form, clips: e.target.value })} data-testid="cg-input-clips" /></label>
          <label style={S.kpiLabel}>Caption style
            <select style={S.input} value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="cg-input-caption">
              {["CARELOGIX_GREEN_GOLD", "CLEAN", "BOLD", "HIGH_CONTRAST", "MINIMAL"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select></label>
        </div>
        <div style={S.note}>By submitting you confirm CareLogix owns or is authorized to use this content (internal rights declaration).</div>
        <button type="button" style={{ ...S.btn, marginTop: 12, opacity: busy ? 0.6 : 1 }} onClick={createAndProcess} disabled={busy} data-testid="cg-create-process">
          {busy ? "Working…" : "Create · Upload · Confirm Rights · Process"}
        </button>
      </div>

      {/* Processing Queue / Projects */}
      <div style={S.sectionTitle}>Projects & Processing Queue</div>
      <div data-testid="cg-projects">
        {projects.length === 0 && <div style={S.note}>No projects yet.</div>}
        {projects.map((p) => (
          <div key={p.project_id} style={S.row} data-testid={`cg-project-${p.project_id}`}>
            <div><strong style={{ color: "#fde68a" }}>{p.name}</strong>
              <div style={S.note}>rights: {p.rights_status} · clips: {p.requested_clip_count}</div></div>
            <span style={S.pill(p.status === "FAILED" ? "#e0483a" : p.status === "HUMAN_REVIEW_REQUIRED" ? "#e8a020" : "#8fdc76")}>{p.status}</span>
          </div>
        ))}
      </div>

      {/* Human Approval Queue */}
      <div style={S.sectionTitle}>Human Approval Queue <span style={S.pill("#e8a020")}>MANDATORY</span></div>
      <div data-testid="cg-review-queue">
        {queue.length === 0 && <div style={S.note}>No clips awaiting approval.</div>}
        {queue.map((c) => (
          <div key={c.clip_id} style={S.row} data-testid={`cg-review-${c.clip_id}`}>
            <div>
              <strong style={{ color: "#fde68a" }}>{c.title}</strong>
              <div style={S.note}>{Math.round(c.duration_ms / 1000)}s · {c.aspect_ratio} · risk <span style={{ color: RISK_COLOR[c.safety?.risk] || "#98a2b6" }}>{c.safety?.risk}</span> · virality {c.virality_score} <span style={S.pill("#98a2b6")}>PREDICTIVE_MODEL</span></div>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <a href={`${CG}/clips/${c.clip_id}/file`} target="_blank" rel="noreferrer" style={S.btnGhost} data-testid={`cg-preview-${c.clip_id}`}>Preview</a>
              <button type="button" style={S.btnGhost} onClick={() => doReject(c)} data-testid={`cg-reject-${c.clip_id}`}>Reject</button>
              <button type="button" style={S.btn} onClick={() => { setApproveClip(c); setConfirmChk(false); setRationale(""); }} data-testid={`cg-approve-open-${c.clip_id}`}>Review & Approve</button>
            </div>
          </div>
        ))}
      </div>

      {/* System Health */}
      <div style={S.sectionTitle}>System Health</div>
      <div style={S.grid} data-testid="cg-health">
        <div style={S.card}><div style={S.kpiLabel}>FFmpeg</div><span style={S.pill(health?.ffmpeg_status === "LIVE" ? "#8fdc76" : "#e8a020")}>{health?.ffmpeg_status}</span></div>
        <div style={S.card}><div style={S.kpiLabel}>Transcription</div><span style={S.pill(health?.transcription?.provider_status === "LIVE" ? "#8fdc76" : "#e8a020")}>{health?.transcription?.provider_status}</span></div>
        <div style={S.card}><div style={S.kpiLabel}>LLM Engine</div><span style={S.pill(health?.llm_provider?.degraded ? "#e8a020" : "#8fdc76")}>{health?.llm_provider?.degraded ? "DEGRADED" : "LIVE"}</span></div>
        <div style={S.card}><div style={S.kpiLabel}>External Publishing</div><span style={S.pill("#98a2b6")}>{health?.external_distribution}</span></div>
      </div>

      {/* Approval modal */}
      {approveClip && (
        <div style={S.modalWrap} data-testid="cg-approve-modal" role="dialog" aria-modal="true">
          <div style={S.modal}>
            <h3 style={{ color: "#fde68a", marginTop: 0, fontFamily: "'Cinzel',serif" }}>Approve: {approveClip.title}</h3>
            <div style={S.note}>Risk: <span style={{ color: RISK_COLOR[approveClip.safety?.risk] }}>{approveClip.safety?.risk}</span> · {Math.round(approveClip.duration_ms / 1000)}s · {approveClip.aspect_ratio}</div>
            {["MEDIUM", "HIGH"].includes(approveClip.safety?.risk) && (
              <label style={{ ...S.kpiLabel, display: "block", marginTop: 10 }}>Approval rationale (required)
                <textarea style={{ ...S.input, minHeight: 70 }} value={rationale} onChange={(e) => setRationale(e.target.value)} data-testid="cg-approve-rationale" /></label>
            )}
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 14, cursor: "pointer" }}>
              <input type="checkbox" checked={confirmChk} onChange={(e) => setConfirmChk(e.target.checked)} data-testid="cg-approve-confirm" />
              <span style={{ fontSize: 13 }}>I explicitly authorize this clip for internal export (human approval).</span>
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
              <button type="button" style={S.btnGhost} onClick={() => setApproveClip(null)} data-testid="cg-approve-cancel">Cancel</button>
              <button type="button" style={S.btn} onClick={doApprove} data-testid="cg-approve-confirm-btn">Approve & Export</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
